import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import { languagesApi } from '../utils/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Slider } from '../components/ui/slider';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Globe, Check, ChevronRight, ChevronLeft, Target, Clock, 
  BookOpen, Sparkles, Zap, Sun, Moon, Coffee, Route, List, X, Search,
  Languages, BookOpenCheck, ScrollText, AlertCircle
} from 'lucide-react';
import axios from 'axios';
import IForteFlow from '../components/onboarding/IForteFlow';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Country code mapping for flags
const CODE_TO_COUNTRY = {
  'es': 'es', 'fr': 'fr', 'de': 'de', 'deu': 'de', 'pt': 'br', 'por': 'br',
  'it': 'it', 'zh': 'cn', 'ja': 'jp', 'ko': 'kr', 'ar': 'sa', 'ru': 'ru',
  'fa': 'ir', 'fas': 'ir', 'tr': 'tr', 'hi': 'in', 'nl': 'nl', 'pl': 'pl',
  'en': 'gb', 'th': 'th', 'vi': 'vn', 'sv': 'se', 'da': 'dk', 'no': 'no',
  'nor': 'no', 'fi': 'fi', 'el': 'gr', 'he': 'il', 'id': 'id', 'ms': 'my', 
  'cs': 'cz', 'hu': 'hu', 'ro': 'ro', 'uk': 'ua', 'bg': 'bg', 'hr': 'hr', 
  'sk': 'sk', 'sl': 'si', 'sr': 'rs', 'lt': 'lt', 'lv': 'lv', 'et': 'ee', 
  'ca': 'es', 'eu': 'es', 'gl': 'es', 'sw': 'ke', 'tl': 'ph', 'bn': 'bd', 
  'ta': 'in', 'te': 'in', 'mr': 'in', 'gu': 'in', 'kn': 'in', 'ml': 'in', 
  'pa': 'in', 'ur': 'pk', 'ne': 'np', 'si': 'lk', 'my': 'mm', 'km': 'kh', 
  'lo': 'la', 'am': 'et', 'yo': 'ng', 'ig': 'ng', 'ha': 'ng', 'zu': 'za', 
  'xh': 'za', 'af': 'za', 'ps': 'af', 'nv': 'us', 'is': 'is', 'ht': 'ht',
  // Additional mappings
  'bs': 'ba', // Bosnian -> Bosnia
  'hy': 'am', // Armenian -> Armenia
  'ka': 'ge', // Georgian -> Georgia
  'kk': 'kz', // Kazakh -> Kazakhstan
  'mk': 'mk', // Macedonian -> North Macedonia
  'sq': 'al', // Albanian -> Albania
  'kea': 'cv', // Kriolu -> Cape Verde
};

// Fixed ordered language lists for pages 1 and 2 (by language code).
// Page 3 uses a dropdown with all remaining languages.
const FEATURED_PAGE_1 = ['en','zh','hi','es','ar','fr','bn','pt','ru','id','ur','de','ja','kea','mr','te'];
const FEATURED_PAGE_2 = ['tr','ko','vi','it','fa','th','sw','nl','pl','uk','he','el','ms','ta','pa','tl'];
const FEATURED_SET = new Set([...FEATURED_PAGE_1, ...FEATURED_PAGE_2]);

// Note: PROFICIENCY_LEVELS, TOPICS, and DAILY_PRESETS are defined inside the
// Onboarding component so their labels can be translated via `t()`.

// Special flags for non-country entities (like indigenous nations)
const SPECIAL_FLAGS = {
  'nv': 'https://upload.wikimedia.org/wikipedia/commons/thumb/c/ce/Official_design_of_the_Navajo_Nation_flag_as_it_was_adopted_on_May_21%2C_1968.svg/120px-Official_design_of_the_Navajo_Nation_flag_as_it_was_adopted_on_May_21%2C_1968.svg.png'
};

// UN official languages
const UN_LANG_CODES = new Set(['ar', 'zh', 'en', 'fr', 'ru', 'es']);

// UN badge — small blue capsule with an olive-wreath-style globe and "UN" text
const UNBadge = () => (
  <span
    title="UN Official Language"
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 3,
      background: '#009EDB',
      color: '#fff',
      borderRadius: 999,
      padding: '1px 7px 1px 5px',
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: '0.04em',
      lineHeight: 1.6,
      flexShrink: 0,
    }}
  >
    {/* Simplified UN globe SVG (olive branches + grid) */}
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="1.8"/>
      <ellipse cx="12" cy="12" rx="4.5" ry="10" stroke="white" strokeWidth="1.4"/>
      <line x1="2" y1="12" x2="22" y2="12" stroke="white" strokeWidth="1.4"/>
      <line x1="4" y1="7" x2="20" y2="7" stroke="white" strokeWidth="1.2"/>
      <line x1="4" y1="17" x2="20" y2="17" stroke="white" strokeWidth="1.2"/>
    </svg>
    UN
</span>
);

// Flag image component
const FlagImage = ({ countryCode, langCode, className = "w-8 h-6" }) => {
  if (langCode && SPECIAL_FLAGS[langCode]) {
    return (
      <img 
        src={SPECIAL_FLAGS[langCode]}
        alt={langCode}
        className={`${className} object-cover rounded`}
        loading="lazy"
      />
    );
  }
  
  // Return null if no valid country code
  if (!countryCode) {
    return null;
  }
  
  // Default to country flag
  return (
    <img 
      src={`https://flagcdn.com/w40/${countryCode}.png`}
      srcSet={`https://flagcdn.com/w80/${countryCode}.png 2x`}
      alt={countryCode}
      className={`${className} object-cover rounded`}
      loading="lazy"
      onError={(e) => { e.target.style.display = 'none'; }}
    />
  );
};

// Step 0: Learning Path Choice (Modern vs Biblical)
const LearningTypeStep = ({ onChoose, t }) => (
  <div className="space-y-8" data-testid="step-learning-type">
    <div className="text-center space-y-2">
      <Languages className="h-12 w-12 mx-auto text-primary" />
      <h2 className="text-2xl font-bold">{t('onboarding.learningType.title')}</h2>
      <p className="text-muted-foreground">{t('onboarding.learningType.subtitle')}</p>
    </div>
    
    <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
      <Card 
        className="cursor-pointer hover:border-primary transition-all hover:shadow-lg group"
        onClick={() => onChoose('modern')}
        data-testid="choose-modern"
      >
        <CardContent className="pt-6 pb-6">
          <div className="text-center space-y-4">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 group-hover:bg-primary/20 transition-colors">
              <Globe className="h-8 w-8 text-primary" />
            </div>
            <div>
              <h3 className="text-xl font-bold mb-2">{t('onboarding.learningType.modernTitle')}</h3>
              <p className="text-muted-foreground text-sm">
                {t('onboarding.learningType.modernDesc')}
              </p>
            </div>
            <div className="flex flex-wrap justify-center gap-2 pt-2">
              {['es', 'fr', 'jp', 'cn', 'sa'].map(code => (
                <img 
                  key={code}
                  src={`https://flagcdn.com/w40/${code === 'cn' ? 'cn' : code === 'jp' ? 'jp' : code === 'sa' ? 'sa' : code}.png`}
                  alt={code}
                  className="w-8 h-6 object-cover rounded opacity-70 group-hover:opacity-100 transition-opacity"
                />
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
      
      <Card 
        className="cursor-pointer hover:border-amber-500 transition-all hover:shadow-lg group border-2"
        onClick={() => onChoose('biblical')}
        data-testid="choose-biblical"
      >
        <CardContent className="pt-6 pb-6">
          <div className="text-center space-y-4">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-500/10 group-hover:bg-amber-500/20 transition-colors">
              <ScrollText className="h-8 w-8 text-amber-500" />
            </div>
            <div>
              <h3 className="text-xl font-bold mb-2">{t('onboarding.learningType.biblicalTitle')}</h3>
              <p className="text-muted-foreground text-sm">
                {t('onboarding.learningType.biblicalDesc')}
              </p>
            </div>
            <div className="flex justify-center gap-4 pt-2">
              <span className="text-3xl opacity-70 group-hover:opacity-100 transition-opacity" style={{fontFamily: 'serif'}}>עברית</span>
              <span className="text-3xl opacity-70 group-hover:opacity-100 transition-opacity" style={{fontFamily: 'serif'}}>Ελληνικά</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  </div>
);

// Step 0.5: Biblical Writing System Familiarity
const BiblicalFamiliarityStep = ({ onChoose, t }) => (
  <div className="space-y-8" data-testid="step-biblical-familiarity">
    <div className="text-center space-y-2">
      <BookOpenCheck className="h-12 w-12 mx-auto text-amber-500" />
      <h2 className="text-2xl font-bold">{t('onboarding.biblicalFamiliarity.title')}</h2>
      <p className="text-muted-foreground">
        {t('onboarding.biblicalFamiliarity.subtitle')}
      </p>
    </div>
    
    {/* Sample text display */}
    <div className="flex justify-center gap-8 text-center">
      <div className="space-y-2">
        <p className="text-3xl font-serif" dir="rtl">בְּרֵאשִׁית בָּרָא אֱלֹהִים</p>
        <p className="text-sm text-muted-foreground">{t('onboarding.biblicalFamiliarity.hebrewLabel')}</p>
      </div>
      <div className="space-y-2">
        <p className="text-3xl font-serif">Ἐν ἀρχῇ ἦν ὁ λόγος</p>
        <p className="text-sm text-muted-foreground">{t('onboarding.biblicalFamiliarity.greekLabel')}</p>
      </div>
    </div>
    
    <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
      <Card 
        className="cursor-pointer hover:border-green-500 transition-all hover:shadow-lg group"
        onClick={() => onChoose('familiar')}
        data-testid="choose-familiar"
      >
        <CardContent className="pt-6 pb-6">
          <div className="text-center space-y-4">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-500/10 group-hover:bg-green-500/20 transition-colors">
              <Check className="h-8 w-8 text-green-500" />
            </div>
            <div>
              <h3 className="text-xl font-bold mb-2">{t('onboarding.biblicalFamiliarity.yesTitle')}</h3>
              <p className="text-muted-foreground text-sm">
                {t('onboarding.biblicalFamiliarity.yesDesc')}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
      
      <Card 
        className="cursor-pointer hover:border-amber-500 transition-all hover:shadow-lg group"
        onClick={() => onChoose('unfamiliar')}
        data-testid="choose-unfamiliar"
      >
        <CardContent className="pt-6 pb-6">
          <div className="text-center space-y-4">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-500/10 group-hover:bg-amber-500/20 transition-colors">
              <BookOpen className="h-8 w-8 text-amber-500" />
            </div>
            <div>
              <h3 className="text-xl font-bold mb-2">{t('onboarding.biblicalFamiliarity.noTitle')}</h3>
              <p className="text-muted-foreground text-sm">
                {t('onboarding.biblicalFamiliarity.noDesc')}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
    
    <p className="text-center text-sm text-muted-foreground">
      {t('onboarding.biblicalFamiliarity.footer')}
    </p>
  </div>
);

// Step 1: Language Selection with Pagination
const LanguageStep = ({ selectedLanguages, onToggle, allLanguages, t, interfaceLocale, user }) => {
  const [langPage, setLangPage] = useState(1);
  const [langSearch, setLangSearch] = useState('');
  const searchRef = React.useRef(null);
  const [dropdownValue, setDropdownValue] = useState('');
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestLangName, setRequestLangName] = useState('');
  const [submittingRequest, setSubmittingRequest] = useState(false);
  
  // Calculate total pages (2 grid pages + 1 dropdown page)
  const totalGridPages = 2;
  const totalPages = 3;

  // Resolve a language's display name in the current interface language.
  // Falls back to the DB-provided English name when the ICU locale database
  // doesn't know the code (e.g. grc, ota) or returns the raw code unchanged.
  const displayNames = useMemo(() => {
    if (typeof Intl === 'undefined' || !Intl.DisplayNames) return null;
    try {
      return new Intl.DisplayNames([interfaceLocale || 'en'], { type: 'language' });
    } catch {
      return null;
    }
  }, [interfaceLocale]);

  const getLocalizedName = useCallback((lang) => {
    if (!displayNames || !lang.code) return lang.name;
    try {
      const resolved = displayNames.of(lang.code);
      // ICU returns the input code unchanged when it can't map it
      if (!resolved || resolved.toLowerCase() === lang.code.toLowerCase()) {
        return lang.name;
      }
      // Capitalise first letter (fr/es/pt return lower-case by convention)
      return resolved.charAt(0).toUpperCase() + resolved.slice(1);
    } catch {
      return lang.name;
    }
  }, [displayNames]);

  // Build a lookup map for fast code → language object resolution.
  const langByCode = useMemo(() => {
    const m = {};
    allLanguages.forEach(l => { m[l.code] = l; });
    return m;
  }, [allLanguages]);

  // Pages 1 and 2 are fixed ordered lists.
  // Page 3 is a dropdown of every language NOT on pages 1 or 2.
  const getPageLanguages = () => {
    if (langPage === 1) return FEATURED_PAGE_1.map(c => langByCode[c]).filter(Boolean);
    if (langPage === 2) return FEATURED_PAGE_2.map(c => langByCode[c]).filter(Boolean);
    return []; // Page 3 uses dropdown
  };

  // Dropdown: all languages not already on pages 1 or 2, sorted by DB English name.
  // Using lang.name (not getLocalizedName) ensures custom names like "Creole - Cape Verde"
  // and "Creole - Haiti" sort under C, and "Chinese - Mandarin" under C, etc.
  const alphabeticalLanguages = useMemo(() => {
    const collator = new Intl.Collator('en', { sensitivity: 'base' });
    return [...allLanguages]
      .filter(l => !FEATURED_SET.has(l.code))
      .sort((a, b) => collator.compare(a.name || '', b.name || ''));
  }, [allLanguages]);

  // Search across all languages (all three tabs combined)
  const searchResults = useMemo(() => {
    if (!langSearch.trim()) return null;
    const q = langSearch.trim().toLowerCase();
    return allLanguages.filter(l =>
      [l.name, l.native_name, l.code].some(v => (v || '').toLowerCase().includes(q))
    );
  }, [allLanguages, langSearch]);
  
  const pageLanguages = getPageLanguages();
  
  const handleDropdownSelect = (langId) => {
    if (langId && langId !== 'placeholder') {
      onToggle(langId);
      setDropdownValue(''); // Reset dropdown
    }
  };

  const handleSubmitLanguageRequest = async () => {
    const name = requestLangName.trim();
    if (!name) return;
    setSubmittingRequest(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/language-requests`,
        { language_name: name },
        { withCredentials: true }
      );
      toast.success(`Request for "${name}" sent to admin!`);
      setRequestLangName('');
      setShowRequestModal(false);
    } catch (err) {
      toast.error('Could not submit request. Please try again.');
    } finally {
      setSubmittingRequest(false);
    }
  };
  
  const getCountryCode = (lang) => {
    return CODE_TO_COUNTRY[lang.code] || null;
  };
  
  return (
    <div className="space-y-6" data-testid="step-languages">
      <div className="text-center space-y-2">
        <Globe className="h-12 w-12 mx-auto text-primary" />
        <h2 className="text-2xl font-bold">{t('onboarding.languages.title')}</h2>
        <p className="text-muted-foreground">{t('onboarding.languages.subtitle')}</p>
      </div>

      {/* ── Persistent search bar ── */}
      <div className="relative max-w-sm mx-auto">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        <input
          ref={searchRef}
          type="text"
          value={langSearch}
          onChange={e => setLangSearch(e.target.value)}
          placeholder="Search any language…"
          className="w-full pl-9 pr-8 py-2 rounded-lg border bg-background text-sm outline-none focus:ring-2 focus:ring-primary/40 transition-shadow"
          data-testid="language-search"
        />
        {langSearch && (
          <button
            onClick={() => { setLangSearch(''); searchRef.current?.focus(); }}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Clear search"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* ── Tab dots — always shown, dimmed during search ── */}
      <div className={`flex justify-center items-center gap-2 transition-opacity ${langSearch ? 'opacity-40 pointer-events-none' : ''}`}>
        {[1, 2, 3].map((page) => (
          <button
            key={page}
            onClick={() => setLangPage(page)}
            className={`w-8 h-8 rounded-full text-sm font-medium transition-colors ${
              langPage === page
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted hover:bg-muted/80'
            }`}
          >
            {page}
          </button>
        ))}
      </div>

      {/* ── Search results (overrides tabs when query is set) ── */}
      {searchResults !== null ? (
        searchResults.length === 0 ? (
          <p className="text-center text-sm text-muted-foreground py-6">No languages match "{langSearch}".</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {searchResults.map(lang => {
              const isSelected = selectedLanguages.includes(lang.language_id);
              const countryCode = getCountryCode(lang);
              return (
                <button
                  key={lang.language_id}
                  onClick={() => onToggle(lang.language_id)}
                  className={`p-4 rounded-xl border-2 transition-all text-left ${
                    isSelected
                      ? 'border-primary bg-primary/10 ring-2 ring-primary/50'
                      : 'border-transparent bg-muted/50 hover:bg-muted'
                  }`}
                  data-testid={`lang-search-${lang.code}`}
                >
                  <div className="flex items-center gap-3">
                    {countryCode || SPECIAL_FLAGS[lang.code] ? (
                      <FlagImage countryCode={countryCode} langCode={lang.code} />
                    ) : (
                      <Globe className="w-8 h-6 text-muted-foreground" />
                    )}
                    <div className="flex-1 min-w-0">
                      {(() => {
                        const top = getLocalizedName(lang);
                        const bottom = lang.native_name || lang.name;
                        const showBottom = bottom && bottom.toLowerCase() !== top.toLowerCase();
                        return (
                          <>
                            <p className="font-medium truncate">{top}</p>
                            {showBottom && (
                              <p className="text-xs text-muted-foreground truncate">{bottom}</p>
                            )}
                            {UN_LANG_CODES.has(lang.code) && (
                              <div className="mt-1"><UNBadge /></div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                    {isSelected && <Check className="h-5 w-5 text-primary flex-shrink-0" />}
                  </div>
                </button>
              );
            })}
          </div>
        )
      ) : (
        <>
          {/* Grid pages (1 and 2) */}
          {langPage <= totalGridPages && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {pageLanguages.map(lang => {
            const isSelected = selectedLanguages.includes(lang.language_id);
            const countryCode = getCountryCode(lang);
            return (
              <button
                key={lang.language_id}
                onClick={() => onToggle(lang.language_id)}
                className={`p-4 rounded-xl border-2 transition-all text-left ${
                  isSelected 
                    ? 'border-primary bg-primary/10 ring-2 ring-primary/50' 
                    : 'border-transparent bg-muted/50 hover:bg-muted'
                }`}
                data-testid={`lang-${lang.code}`}
              >
                <div className="flex items-center gap-3">
                  {countryCode || SPECIAL_FLAGS[lang.code] ? (
                    <FlagImage countryCode={countryCode} langCode={lang.code} />
                  ) : (
                    <Globe className="w-8 h-6 text-muted-foreground" />
                  )}
                  <div className="flex-1 min-w-0">
                    {/* Top line: name in the interface language; bottom line
                        stays the native/original script so users can still
                        recognise their language regardless of UI locale.
                        Skip the bottom line when it would just duplicate the
                        top (e.g. Italian interface viewing "Italiano"). */}
                    {(() => {
                      const top = getLocalizedName(lang);
                      const bottom = lang.native_name || lang.name;
                      const showBottom = bottom && bottom.toLowerCase() !== top.toLowerCase();
                      return (
                        <>
                          <p className="font-medium truncate">{top}</p>
                          {showBottom && (
                            <p className="text-xs text-muted-foreground truncate">{bottom}</p>
                          )}
                          {UN_LANG_CODES.has(lang.code) && (
                            <div className="mt-1">
                              <UNBadge />
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                  {isSelected && (
                    <Check className="h-5 w-5 text-primary flex-shrink-0" />
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}
      
      {/* Dropdown page (3) */}
      {langPage === 3 && (
        <div className="max-w-md mx-auto space-y-4">
          <div className="flex items-center gap-2 justify-center text-muted-foreground">
            <List className="h-5 w-5" />
            <span>{t('onboarding.languages.allAZ')}</span>
          </div>
          
          <Select value={dropdownValue} onValueChange={handleDropdownSelect}>
            <SelectTrigger className="w-full" data-testid="all-languages-dropdown">
              <SelectValue placeholder={t('onboarding.languages.selectPlaceholder')} />
            </SelectTrigger>
            <SelectContent className="max-h-[300px]">
              {alphabeticalLanguages.map(lang => {
                const isSelected = selectedLanguages.includes(lang.language_id);
                return (
                  <SelectItem 
                    key={lang.language_id} 
                    value={lang.language_id}
                    disabled={isSelected}
                  >
                    <span className="flex items-center gap-2">
                      {getLocalizedName(lang)}
                      {lang.native_name && lang.native_name !== getLocalizedName(lang) && (
                        <span className="text-muted-foreground">({lang.native_name})</span>
                      )}
                      {isSelected && <Check className="h-4 w-4 text-primary ml-2" />}
                    </span>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
          
          <p className="text-center text-sm text-muted-foreground">
            {t('onboarding.languages.cantFind')}
          </p>

          {/* Language Not Found button */}
          <button
            data-testid="language-not-found-btn"
            onClick={() => setShowRequestModal(true)}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 border-red-500 bg-red-500/10 text-red-500 font-semibold hover:bg-red-500/20 transition-colors"
          >
            <AlertCircle className="h-5 w-5" />
            Language Not Found
          </button>
        </div>
      )}
        </>
      )}

      {/* Language Not Found Modal */}
      {showRequestModal && (
        <div
          data-testid="lang-request-modal"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setShowRequestModal(false); }}
        >
          <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-5">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-red-500" />
                Request a Language
              </h3>
              <button
                onClick={() => setShowRequestModal(false)}
                className="text-muted-foreground hover:text-foreground transition"
                data-testid="close-lang-request-modal"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="text-sm text-muted-foreground">
              Don't see your language? Let us know and we'll add it!
            </p>
            <div className="space-y-2">
              <label className="text-sm font-medium">Language name</label>
              <input
                data-testid="lang-request-input"
                type="text"
                value={requestLangName}
                onChange={e => setRequestLangName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && requestLangName.trim()) handleSubmitLanguageRequest(); }}
                placeholder="e.g. Tigrinya, Wolof, Romani…"
                className="w-full px-4 py-2.5 rounded-lg border bg-background text-sm outline-none focus:ring-2 focus:ring-primary/40"
                autoFocus
              />
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowRequestModal(false)}
                className="px-4 py-2 rounded-lg border text-sm hover:bg-muted transition"
                data-testid="cancel-lang-request"
              >
                Cancel
              </button>
              <button
                data-testid="submit-lang-request"
                onClick={handleSubmitLanguageRequest}
                disabled={!requestLangName.trim() || submittingRequest}
                className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-medium transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submittingRequest ? 'Sending…' : 'Send Request'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Selected languages display */}
      {selectedLanguages.length > 0 && (
        <div className="space-y-2">
          <p className="text-center text-sm text-muted-foreground">
            {t(selectedLanguages.length > 1 ? 'onboarding.languages.selectedPlural' : 'onboarding.languages.selectedSingle', { count: selectedLanguages.length })}
          </p>
          <div className="flex flex-wrap justify-center gap-2">
            {selectedLanguages.map(langId => {
              const lang = allLanguages.find(l => l.language_id === langId);
              if (!lang) return null;
              const countryCode = getCountryCode(lang);
              return (
                <button
                  key={langId}
                  onClick={() => onToggle(langId)}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-sm hover:bg-primary/20 transition-colors"
                >
                  {(countryCode || SPECIAL_FLAGS[lang.code]) && <FlagImage countryCode={countryCode} langCode={lang.code} className="w-5 h-4" />}
                  <span>{getLocalizedName(lang)}</span>
                  <X className="h-4 w-4" />
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// Step 2: Proficiency
const ProficiencyStep = ({ selectedLanguages, proficiencies, onSetProficiency, allLanguages, t, proficiencyLevels, interfaceLocale }) => {
  const displayNames = useMemo(() => {
    if (typeof Intl === 'undefined' || !Intl.DisplayNames) return null;
    try { return new Intl.DisplayNames([interfaceLocale || 'en'], { type: 'language' }); }
    catch { return null; }
  }, [interfaceLocale]);

  const localizedName = (lang) => {
    if (!displayNames || !lang?.code) return lang?.name;
    try {
      const r = displayNames.of(lang.code);
      if (!r || r.toLowerCase() === lang.code.toLowerCase()) return lang.name;
      return r.charAt(0).toUpperCase() + r.slice(1);
    } catch { return lang.name; }
  };

  const getLanguageDisplay = (langId) => {
    const lang = allLanguages.find(l => l.language_id === langId);
    if (lang) {
      return {
        name: localizedName(lang),
        nativeName: lang.native_name || lang.name,
        code: lang.code,
        countryCode: CODE_TO_COUNTRY[lang.code] || null
      };
    }
    return { name: langId, nativeName: null, code: null, countryCode: null };
  };
  
  return (
    <div className="space-y-6" data-testid="step-proficiency">
      <div className="text-center space-y-2">
        <Target className="h-12 w-12 mx-auto text-primary" />
        <h2 className="text-2xl font-bold">{t('onboarding.proficiency.title')}</h2>
        <p className="text-muted-foreground">{t('onboarding.proficiency.subtitle')}</p>
      </div>
      
      <div className="space-y-6">
        {selectedLanguages.map(langId => {
          const lang = getLanguageDisplay(langId);
          const currentLevel = proficiencies[langId] || 'beginner';
          
          return (
            <div key={langId} className="p-4 rounded-xl bg-muted/30">
              <div className="flex items-center gap-3 mb-4">
                {(lang.countryCode || SPECIAL_FLAGS[lang.code]) ? (
                  <FlagImage countryCode={lang.countryCode} langCode={lang.code} className="w-8 h-6" />
                ) : (
                  <Globe className="w-8 h-6 text-muted-foreground" />
                )}
                <span className="font-semibold">{lang.name}</span>
              </div>
              
              <div className="grid grid-cols-3 gap-2">
                {proficiencyLevels.map(level => (
                  <button
                    key={level.id}
                    onClick={() => onSetProficiency(langId, level.id)}
                    className={`p-3 rounded-lg text-center transition-all ${
                      currentLevel === level.id
                        ? 'bg-primary text-primary-foreground ring-2 ring-primary'
                        : 'bg-muted/50 hover:bg-muted'
                    }`}
                    data-testid={`prof-${langId}-${level.id}`}
                  >
                    <div className="text-2xl mb-1">{level.icon}</div>
                    <div className="font-medium text-sm">{level.label}</div>
                    <div className="text-xs opacity-70">{level.description}</div>
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Step 3: Goals
const GoalsStep = ({ dailyMinutes, dailyWords, onSetGoal, t, dailyPresets }) => (
  <div className="space-y-8" data-testid="step-goals">
    <div className="text-center space-y-2">
      <Zap className="h-12 w-12 mx-auto text-primary" />
      <h2 className="text-2xl font-bold">{t('onboarding.goals.title')}</h2>
      <p className="text-muted-foreground">{t('onboarding.goals.subtitle')}</p>
    </div>
    
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {dailyPresets.map(preset => (
        <button
          key={preset.label}
          onClick={() => onSetGoal(preset.minutes, preset.words)}
          className={`p-4 rounded-xl text-center transition-all ${
            dailyMinutes === preset.minutes
              ? 'bg-primary text-primary-foreground ring-2 ring-primary'
              : 'bg-muted/50 hover:bg-muted'
          }`}
          data-testid={`preset-${preset.id}`}
        >
          <div className="mb-2">{preset.icon}</div>
          <div className="font-semibold">{preset.label}</div>
          <div className="text-sm opacity-70">{t('onboarding.goals.minPerDay', { minutes: preset.minutes })}</div>
          <div className="text-xs opacity-50">{t('onboarding.goals.wordsLabel', { count: preset.words })}</div>
        </button>
      ))}
    </div>
    
    <Card>
      <CardContent className="pt-6 space-y-6">
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <span className="font-medium">{t('onboarding.goals.dailyTime')}</span>
            <span className="text-primary font-semibold">{t('onboarding.goals.minutesUnit', { count: dailyMinutes })}</span>
          </div>
          <Slider
            value={[dailyMinutes]}
            onValueChange={([val]) => onSetGoal(val, dailyWords)}
            min={5}
            max={120}
            step={5}
            className="py-2"
          />
        </div>
        
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <span className="font-medium">{t('onboarding.goals.dailyWords')}</span>
            <span className="text-primary font-semibold">{t('onboarding.goals.wordsUnit', { count: dailyWords })}</span>
          </div>
          <Slider
            value={[dailyWords]}
            onValueChange={([val]) => onSetGoal(dailyMinutes, val)}
            min={5}
            max={100}
            step={5}
            className="py-2"
          />
        </div>
      </CardContent>
    </Card>
  </div>
);

// Step 4: Schedule
const ScheduleStep = ({ reminderEnabled, reminderTime, onSetReminder, t }) => (
  <div className="space-y-8" data-testid="step-schedule">
    <div className="text-center space-y-2">
      <Clock className="h-12 w-12 mx-auto text-primary" />
      <h2 className="text-2xl font-bold">{t('onboarding.schedule.title')}</h2>
      <p className="text-muted-foreground">{t('onboarding.schedule.subtitle')}</p>
    </div>
    
    <Card className="max-w-md mx-auto">
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium">{t('onboarding.schedule.reminderLabel')}</p>
            <p className="text-sm text-muted-foreground">{t('onboarding.schedule.reminderDesc')}</p>
          </div>
          <Switch
            checked={reminderEnabled}
            onCheckedChange={(checked) => onSetReminder(checked, reminderTime)}
            data-testid="reminder-toggle"
          />
        </div>
        
        {reminderEnabled && (
          <div className="mt-4 pt-4 border-t">
            <label className="text-sm font-medium">{t('onboarding.schedule.reminderTime')}</label>
            <input
              type="time"
              value={reminderTime}
              onChange={(e) => onSetReminder(true, e.target.value)}
              className="mt-2 w-full p-2 rounded-md border bg-background"
              data-testid="reminder-time"
            />
          </div>
        )}
      </CardContent>
    </Card>
    
    <p className="text-center text-sm text-muted-foreground">
      {t('onboarding.schedule.footer')}
    </p>
  </div>
);

// Step 5: Path Choice
const PathChoiceStep = ({ onChoose, forgeStoryLanguages, userLanguageCodes, t }) => {
  // Find if any of the user's selected languages is available for Forge Story
  const availableCodes = (forgeStoryLanguages || []).map(l => l.code);
  const availableNames = (forgeStoryLanguages || []).map(l => l.name);
  const forgeAvailable = userLanguageCodes.some(c => availableCodes.includes(c));
  const availableListText = availableNames.join(', ') || t('onboarding.path.forgeStoryUnavailableNone');

  return (
  <div className="space-y-8" data-testid="step-path">
    <div className="text-center space-y-2">
      <Route className="h-12 w-12 mx-auto text-primary" />
      <h2 className="text-2xl font-bold">{t('onboarding.path.title')}</h2>
      <p className="text-muted-foreground whitespace-pre-line">
        {t('onboarding.path.subtitle')}
      </p>
    </div>
    
    <div className="grid md:grid-cols-3 gap-4 max-w-5xl mx-auto">
      <Card 
        className="cursor-pointer hover:border-primary transition-all hover:shadow-lg"
        onClick={() => onChoose('custom')}
        data-testid="path-custom"
      >
        <CardContent className="pt-6">
          <div className="flex flex-col items-start gap-3">
            <div className="p-3 rounded-lg bg-primary/10">
              <BookOpen className="h-8 w-8 text-primary" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold mb-2">{t('onboarding.path.customTitle')}</h3>
              <p className="text-muted-foreground text-sm">
                {t('onboarding.path.customDesc')}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
      
      <Card 
        className="cursor-pointer hover:border-red-500 transition-all hover:shadow-lg border-2"
        onClick={() => onChoose('iforte')}
        data-testid="path-iforte"
      >
        <CardContent className="pt-6">
          <div className="flex flex-col items-start gap-3">
            <div className="p-3 rounded-lg bg-red-500/10">
              <Sparkles className="h-8 w-8 text-red-500" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold mb-2">
                {t('onboarding.path.ifortTitle')}
              </h3>
              <p className="text-muted-foreground text-sm">
                {t('onboarding.path.ifortDesc')}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card 
        className={`transition-all ${
          forgeAvailable
            ? 'cursor-pointer hover:border-indigo-500 hover:shadow-lg'
            : 'opacity-60 cursor-not-allowed'
        }`}
        onClick={() => { if (forgeAvailable) onChoose('forge_story'); }}
        data-testid="path-forge-story"
      >
        <CardContent className="pt-6">
          <div className="flex flex-col items-start gap-3">
            <div className="p-3 rounded-lg bg-indigo-500/10">
              <ScrollText className="h-8 w-8 text-indigo-500" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold mb-2">{t('onboarding.path.forgeStoryTitle')}</h3>
              <p className="text-muted-foreground text-sm">
                {t('onboarding.path.forgeStoryDesc')}
              </p>
              {!forgeAvailable && (
                <p
                  className="text-xs text-amber-400 mt-3 italic"
                  data-testid="forge-story-unavailable-msg"
                >
                  {t('onboarding.path.forgeStoryUnavailable', { list: availableListText })}
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  </div>
  );
};

// Step 6: Topics (Custom path)
const TopicsStep = ({ selectedTopics, onToggle, t, topics }) => (
  <div className="space-y-6" data-testid="step-topics">
    <div className="text-center space-y-2">
      <BookOpen className="h-12 w-12 mx-auto text-primary" />
      <h2 className="text-2xl font-bold">{t('onboarding.topics.title')}</h2>
      <p className="text-muted-foreground">{t('onboarding.topics.subtitle')}</p>
    </div>
    
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {topics.map(topic => {
        const isSelected = selectedTopics.includes(topic.id);
        return (
          <button
            key={topic.id}
            onClick={() => onToggle(topic.id)}
            className={`p-4 rounded-xl text-center transition-all ${
              isSelected
                ? 'bg-primary text-primary-foreground ring-2 ring-primary'
                : 'bg-muted/50 hover:bg-muted'
            }`}
            data-testid={`topic-${topic.id}`}
          >
            <div className="text-3xl mb-2">{topic.icon}</div>
            <div className="font-medium text-sm">{topic.label}</div>
          </button>
        );
      })}
    </div>
    
    {selectedTopics.length > 0 && (
      <p className="text-center text-sm text-muted-foreground">
        {selectedTopics.length} topic{selectedTopics.length > 1 ? 's' : ''} selected
      </p>
    )}
  </div>
);

// Main Onboarding Component
export default function Onboarding() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isRevisit = new URLSearchParams(location.search).get('revisit') === 'true';
  const { t, locale, setLocale } = useI18n();

  // Static option lists translated at render time so they react to interface
  // language changes without remounting.
  const proficiencyLevels = useMemo(() => [
    { id: 'beginner',     label: t('onboarding.proficiency.beginner'),     description: t('onboarding.proficiency.beginnerDesc'),     icon: '🌱', targetWords: 1000 },
    { id: 'intermediate', label: t('onboarding.proficiency.intermediate'), description: t('onboarding.proficiency.intermediateDesc'), icon: '🌿', targetWords: 3000 },
    { id: 'advanced',     label: t('onboarding.proficiency.advanced'),     description: t('onboarding.proficiency.advancedDesc'),     icon: '🌳', targetWords: 6000 },
  ], [t]);

  const topics = useMemo(() => [
    { id: 'news',          label: t('onboarding.topics.news'),          icon: '📰' },
    { id: 'literature',    label: t('onboarding.topics.literature'),    icon: '📚' },
    { id: 'business',      label: t('onboarding.topics.business'),      icon: '💼' },
    { id: 'travel',        label: t('onboarding.topics.travel'),        icon: '✈️' },
    { id: 'science',       label: t('onboarding.topics.science'),       icon: '🔬' },
    { id: 'sports',        label: t('onboarding.topics.sports'),        icon: '⚽' },
    { id: 'food',          label: t('onboarding.topics.food'),          icon: '🍳' },
    { id: 'entertainment', label: t('onboarding.topics.entertainment'), icon: '🎬' },
    { id: 'dialogue',      label: t('onboarding.topics.dialogue'),      icon: '💬' },
    { id: 'routine',       label: t('onboarding.topics.routine'),       icon: '🌅' },
  ], [t]);

  const dailyPresets = useMemo(() => [
    { id: 'light',     minutes: 5,  words: 10, label: t('onboarding.goals.presetLight'),     icon: <Coffee className="h-5 w-5" /> },
    { id: 'regular',   minutes: 15, words: 20, label: t('onboarding.goals.presetRegular'),   icon: <Sun className="h-5 w-5" /> },
    { id: 'committed', minutes: 30, words: 35, label: t('onboarding.goals.presetCommitted'), icon: <Zap className="h-5 w-5" /> },
    { id: 'intensive', minutes: 60, words: 50, label: t('onboarding.goals.presetIntensive'), icon: <Moon className="h-5 w-5" /> },
  ], [t]);

  // Load initial state from sessionStorage (to persist across interface language changes)
  const getInitialState = (key, defaultValue) => {
    try {
      const saved = sessionStorage.getItem(`onboarding_${key}`);
      return saved ? JSON.parse(saved) : defaultValue;
    } catch {
      return defaultValue;
    }
  };
  
  // Core state
  const [step, setStep] = useState(() => getInitialState('step', 0));
  const [loading, setLoading] = useState(false);
  const [allLanguages, setAllLanguages] = useState([]);
  const [forgeStoryLanguages, setForgeStoryLanguages] = useState([]);
  
  // Learning type state (modern vs biblical)
  const [learningType, setLearningType] = useState(() => getInitialState('learningType', null));
  const [biblicalFamiliarity, setBiblicalFamiliarity] = useState(() => getInitialState('biblicalFamiliarity', null));
  
  // Form data
  const [selectedLanguages, setSelectedLanguages] = useState(() => getInitialState('selectedLanguages', []));
  const [proficiencies, setProficiencies] = useState(() => getInitialState('proficiencies', {}));
  const [dailyMinutes, setDailyMinutes] = useState(() => getInitialState('dailyMinutes', 15));
  const [dailyWords, setDailyWords] = useState(() => getInitialState('dailyWords', 20));
  const [reminderEnabled, setReminderEnabled] = useState(() => getInitialState('reminderEnabled', false));
  const [reminderTime, setReminderTime] = useState(() => getInitialState('reminderTime', '09:00'));
  const [learningPath, setLearningPath] = useState(() => getInitialState('learningPath', null));
  const [selectedTopics, setSelectedTopics] = useState(() => getInitialState('selectedTopics', []));
  
  // Persist state to sessionStorage when it changes
  useEffect(() => {
    sessionStorage.setItem('onboarding_step', JSON.stringify(step));
  }, [step]);
  
  useEffect(() => {
    sessionStorage.setItem('onboarding_learningType', JSON.stringify(learningType));
  }, [learningType]);
  
  useEffect(() => {
    sessionStorage.setItem('onboarding_selectedLanguages', JSON.stringify(selectedLanguages));
  }, [selectedLanguages]);
  
  useEffect(() => {
    sessionStorage.setItem('onboarding_proficiencies', JSON.stringify(proficiencies));
  }, [proficiencies]);
  
  useEffect(() => {
    sessionStorage.setItem('onboarding_dailyMinutes', JSON.stringify(dailyMinutes));
    sessionStorage.setItem('onboarding_dailyWords', JSON.stringify(dailyWords));
  }, [dailyMinutes, dailyWords]);
  
  useEffect(() => {
    sessionStorage.setItem('onboarding_learningPath', JSON.stringify(learningPath));
  }, [learningPath]);
  
  useEffect(() => {
    sessionStorage.setItem('onboarding_selectedTopics', JSON.stringify(selectedTopics));
  }, [selectedTopics]);
  
  // Skip onboarding if user already has active languages — but NOT when
  // the user deliberately navigates here via ?revisit=true (sidebar link).
  useEffect(() => {
    if (isRevisit) return;   // user chose to come here — never redirect
    const checkExistingLanguages = async () => {
      try {
        const res = await languagesApi.getUserLanguages();
        const userLangs = res.data || [];
        const active = userLangs.filter(ul => !ul.hidden);
        if (active.length > 0) {
          navigate('/dashboard', { replace: true });
          return;
        }
      } catch {
        // Continue with onboarding if check fails
      }
    };
    checkExistingLanguages();
  }, [navigate, isRevisit]);

  // Load languages from DB ordered by popularity
  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/languages/popular`, { withCredentials: true });
        // Filter out test languages and biblical languages (they have their own path)
        const biblicalCodes = ['biblical_hebrew', 'biblical_greek', 'hbo', 'grc', 'hebrew_biblical', 'greek_biblical'];
        const filtered = res.data.filter(l => 
          !['test', 'xyz'].includes(l.code?.toLowerCase()) &&
          !biblicalCodes.includes(l.code?.toLowerCase()) &&
          !l.name?.toLowerCase().includes('biblical')
        );
        setAllLanguages(filtered);
      } catch (err) {
        console.error('Error fetching languages:', err);
        // Fallback to regular endpoint
        try {
          const fallback = await languagesApi.getAll();
          const biblicalCodes = ['biblical_hebrew', 'biblical_greek', 'hbo', 'grc', 'hebrew_biblical', 'greek_biblical'];
          const filtered = fallback.data.filter(l => 
            !biblicalCodes.includes(l.code?.toLowerCase()) &&
            !l.name?.toLowerCase().includes('biblical')
          );
          setAllLanguages(filtered);
        } catch (e) {
          console.error('Fallback also failed:', e);
        }
      }
    };
    fetchLanguages();
  }, []);

  // Load list of languages that have Forge Stories available
  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/forge-stories/languages`, { withCredentials: true })
      .then(res => setForgeStoryLanguages(res.data.languages || []))
      .catch(() => setForgeStoryLanguages([]));
  }, []);
  
  // Handlers
  const toggleLanguage = useCallback((langId) => {
    if (selectedLanguages.includes(langId)) {
      setSelectedLanguages(prev => prev.filter(id => id !== langId));
      setProficiencies(prev => {
        const newProf = { ...prev };
        delete newProf[langId];
        return newProf;
      });
    } else {
      setSelectedLanguages(prev => [...prev, langId]);
      setProficiencies(prev => ({ ...prev, [langId]: 'beginner' }));
    }
  }, [selectedLanguages]);
  
  const setProficiency = useCallback((langId, level) => {
    setProficiencies(prev => ({ ...prev, [langId]: level }));
  }, []);
  
  const setGoal = useCallback((minutes, words) => {
    setDailyMinutes(minutes);
    setDailyWords(words);
  }, []);
  
  const setReminder = useCallback((enabled, time) => {
    setReminderEnabled(enabled);
    if (time) setReminderTime(time);
  }, []);
  
  const toggleTopic = useCallback((topicId) => {
    setSelectedTopics(prev => 
      prev.includes(topicId) 
        ? prev.filter(id => id !== topicId)
        : [...prev, topicId]
    );
  }, []);
  
  // Handle learning type choice (modern vs biblical)
  const handleLearningTypeChoice = useCallback((type) => {
    setLearningType(type);
    if (type === 'modern') {
      // Go to language selection (step 1)
      setStep(1);
    } else if (type === 'biblical') {
      // Go to biblical familiarity question (step 0.5)
      setStep(0.5);
    }
  }, []);
  
  // Handle biblical familiarity choice
  const handleBiblicalFamiliarityChoice = useCallback(async (familiarity) => {
    setBiblicalFamiliarity(familiarity);
    setLoading(true);
    try {
      // Resolve the biblical language IDs from the languages list. We need them
      // to satisfy `OnboardingData.languages` (which requires at least one entry)
      // and to enroll the user so /bible-courses + /alphabet-courses load
      // their content correctly.
      const allLangsResp = await axios.get(`${BACKEND_URL}/api/languages`, { withCredentials: true });
      const allLangs = Array.isArray(allLangsResp.data) ? allLangsResp.data : (allLangsResp.data?.languages || []);
      const hbo = allLangs.find(l => (l.code || '').toLowerCase() === 'hbo');
      const grc = allLangs.find(l => (l.code || '').toLowerCase() === 'grc');
      const biblicalLangs = [hbo, grc].filter(Boolean);
      if (biblicalLangs.length === 0) {
        toast.error(t('onboarding.biblicalFamiliarity.missingLanguages'));
        setLoading(false);
        return;
      }

      const data = {
        languages: biblicalLangs.map((lang, idx) => ({
          language_id: lang.language_id,
          proficiency: 'beginner',
          is_primary: idx === 0,
        })),
        daily_goal_minutes: dailyMinutes,
        daily_word_target: dailyWords,
        reminder_time: reminderEnabled ? reminderTime : null,
        reminder_enabled: reminderEnabled,
        learning_path: 'biblical',
        topics: [],
        iforte_data: null,
      };

      await axios.post(`${BACKEND_URL}/api/onboarding/complete`, data, { withCredentials: true });

      // Clear onboarding state from sessionStorage so we don't re-show the wizard.
      Object.keys(sessionStorage).filter(k => k.startsWith('onboarding_')).forEach(k => sessionStorage.removeItem(k));

      toast.success(t('onboarding.biblicalFamiliarity.welcomeToast'));

      // Hard-navigate so AuthContext refreshes `onboarding_completed=true`.
      // Soft navigate would still hit ProtectedRoute with a stale `user` and
      // bounce us back to /onboarding.
      const target = familiarity === 'familiar' ? '/bible-courses' : '/alphabet-courses';
      window.location.href = target;
    } catch (err) {
      console.error('Error completing biblical onboarding:', err);
      toast.error(t('onboarding.complete.savePreferencesError'));
      setLoading(false);
    }
  }, [dailyMinutes, dailyWords, reminderEnabled, reminderTime]);
  
  const handlePathChoice = useCallback(async (path) => {
    setLearningPath(path);
    if (path === 'forge_story') {
      // Forge Story path is self-contained: save preferences and jump straight to stories.
      setLoading(true);
      try {
        const primaryLangId = selectedLanguages[0];
        const primaryLang = allLanguages.find(l => l.language_id === primaryLangId);

        const data = {
          languages: selectedLanguages.map((langId, idx) => ({
            language_id: langId,
            proficiency: proficiencies[langId] || 'beginner',
            is_primary: idx === 0
          })),
          daily_goal_minutes: dailyMinutes,
          daily_word_target: dailyWords,
          reminder_time: reminderEnabled ? reminderTime : null,
          reminder_enabled: reminderEnabled,
          learning_path: 'forge_story',
          topics: [],
          iforte_data: null,
        };

        await axios.post(`${BACKEND_URL}/api/onboarding/complete`, data, { withCredentials: true });

        toast.success(t('onboarding.path.forgeStoryWelcome'));
        Object.keys(sessionStorage).filter(k => k.startsWith('onboarding_')).forEach(k => sessionStorage.removeItem(k));

        const langCode = primaryLang?.code || 'es';
        window.location.href = `/forge-stories/${langCode}`;
      } catch (err) {
        console.error('Error completing onboarding:', err);
        toast.error(t('onboarding.complete.savePreferencesError'));
        setLoading(false);
      }
      return;
    }
    setStep(6);
  }, [selectedLanguages, proficiencies, dailyMinutes, dailyWords, reminderEnabled, reminderTime, allLanguages]);
  
  // Complete onboarding (save preferences)
  const completeOnboarding = useCallback(async () => {
    const data = {
      languages: selectedLanguages.map((langId, idx) => ({
        language_id: langId,
        proficiency: proficiencies[langId] || 'beginner',
        is_primary: idx === 0
      })),
      daily_goal_minutes: dailyMinutes,
      daily_word_target: dailyWords,
      reminder_time: reminderEnabled ? reminderTime : null,
      reminder_enabled: reminderEnabled,
      learning_path: learningPath,
      topics: learningPath === 'custom' ? selectedTopics : [],
      iforte_data: null
    };

    await axios.post(`${BACKEND_URL}/api/onboarding/complete`, data, {
      withCredentials: true
    });
  }, [selectedLanguages, proficiencies, dailyMinutes, dailyWords, reminderEnabled, reminderTime, learningPath, selectedTopics]);
  
  const handleCustomComplete = async () => {
    setLoading(true);
    try {
      // 1. Save onboarding preferences — this is the ONLY step that must block.
      await completeOnboarding();
    } catch (err) {
      console.error('Error completing onboarding:', err);
      toast.error(t('onboarding.complete.savePreferencesError'));
      setLoading(false);
      return;
    }

    // 2. Kick off starter-text generation in the background — do NOT block
    //    navigation on it. LLM calls for multiple languages can easily take
    //    20-60s, which previously made the UI feel stuck. If generation
    //    fails, we only warn; preferences are already saved.
    if (selectedTopics.length > 0 && selectedLanguages.length > 0) {
      const randomTopic = topics.find(
        tp => tp.id === selectedTopics[Math.floor(Math.random() * selectedTopics.length)]
      );
      const languageConfigs = selectedLanguages.map(langId => ({
        language_id: langId,
        proficiency: proficiencies[langId] || 'beginner',
      }));

      // Fire-and-forget. The dashboard will pick up the generated texts on
      // its next fetch cycle.
      axios.post(
        `${BACKEND_URL}/api/texts/generate-batch-from-topic`,
        {
          topic: randomTopic?.label || 'Daily Life',
          languages: languageConfigs,
          word_count: 200,
        },
        { withCredentials: true }
      ).catch(genErr => {
        console.error('Background text generation failed:', genErr);
      });

      toast.info(
        t('onboarding.complete.generating', {
          topic: randomTopic?.label || t('onboarding.complete.fallbackTopic'),
        })
      );
    }

    toast.success(t('onboarding.complete.welcomeFinal'));
    // Clear onboarding state from sessionStorage
    Object.keys(sessionStorage)
      .filter(k => k.startsWith('onboarding_'))
      .forEach(k => sessionStorage.removeItem(k));
    window.location.href = '/dashboard';
  };
  
  // Navigation
  const canProceed = () => {
    switch (step) {
      case 0: return learningType !== null;
      case 0.5: return biblicalFamiliarity !== null;
      case 1: return selectedLanguages.length > 0;
      case 2: return selectedLanguages.every(id => proficiencies[id]);
      case 3: return dailyMinutes > 0 && dailyWords > 0;
      case 4: return true;
      case 5: return learningPath !== null;
      case 6: return learningPath === 'custom' ? selectedTopics.length > 0 : true;
      default: return false;
    }
  };
  
  const handleNext = () => setStep(prev => prev + 1);
  const handleBack = () => {
    if (step === 6 && learningPath) {
      setLearningPath(null);
      setStep(5);
    } else if (step === 1 && learningType === 'modern') {
      // Go back to learning type choice
      setLearningType(null);
      setStep(0);
    } else if (step === 0.5) {
      // Go back to learning type choice
      setLearningType(null);
      setBiblicalFamiliarity(null);
      setStep(0);
    } else {
      setStep(prev => prev - 1);
    }
  };
  
  // Calculate progress
  const getTotalSteps = () => {
    if (learningType === 'biblical') return 2; // Just 2 steps for biblical path
    if (!learningPath) return 6; // 0 + 5 steps for modern
    if (learningPath === 'custom') return 7; // 0 + 6 steps
    return 7; // iFORTE handles its own steps internally
  };
  
  const getStepLabel = () => {
    if (step === 0) return t('onboarding.header.chooseFocus');
    if (step === 0.5) return t('onboarding.header.writingSystemCheck');
    if (step <= 4) return t('onboarding.header.stepLabel', { current: step, total: getTotalSteps() - 1 });
    if (step === 5) return t('onboarding.header.choosePath');
    if (learningPath === 'custom' && step === 6) return t('onboarding.header.stepLabel', { current: 6, total: getTotalSteps() - 1 });
    if (learningPath === 'iforte') return t('onboarding.header.ifortSetup');
    return t('onboarding.header.stepLabelShort', { current: step });
  };
  
  const getProgressPercent = () => {
    if (step === 0) return 0;
    if (step === 0.5) return 50;
    const total = getTotalSteps();
    return (Math.min(step, total) / total) * 100;
  };
  
  // Render current step
  const renderStep = () => {
    switch (step) {
      case 0:
        return <LearningTypeStep onChoose={handleLearningTypeChoice} t={t} />;
      case 0.5:
        return <BiblicalFamiliarityStep onChoose={handleBiblicalFamiliarityChoice} t={t} />;
      case 1:
        return <LanguageStep selectedLanguages={selectedLanguages} onToggle={toggleLanguage} allLanguages={allLanguages} t={t} interfaceLocale={locale} user={user} />;
      case 2:
        return <ProficiencyStep selectedLanguages={selectedLanguages} proficiencies={proficiencies} onSetProficiency={setProficiency} allLanguages={allLanguages} t={t} proficiencyLevels={proficiencyLevels} interfaceLocale={locale} />;
      case 3:
        return <GoalsStep dailyMinutes={dailyMinutes} dailyWords={dailyWords} onSetGoal={setGoal} t={t} dailyPresets={dailyPresets} />;
      case 4:
        return <ScheduleStep reminderEnabled={reminderEnabled} reminderTime={reminderTime} onSetReminder={setReminder} t={t} />;
      case 5:
        return <PathChoiceStep
          onChoose={handlePathChoice}
          forgeStoryLanguages={forgeStoryLanguages}
          userLanguageCodes={selectedLanguages.map(id => allLanguages.find(l => l.language_id === id)?.code).filter(Boolean)}
          t={t}
        />;
      case 6:
        if (learningPath === 'custom') {
          return <TopicsStep selectedTopics={selectedTopics} onToggle={toggleTopic} t={t} topics={topics} />;
        }
        // iFORTE flow - completely self-contained
        return (
          <IForteFlow
            selectedLanguages={selectedLanguages}
            onComplete={completeOnboarding}
            onSwitchToCustom={() => {
              setLearningPath('custom');
              setStep(6);
            }}
            onBack={() => {
              setLearningPath(null);
              setStep(5);
            }}
          />
        );
      default:
        return null;
    }
  };
  
  // Check if we should show navigation footer
  const showNavFooter = (step >= 1 && step <= 5) || (step === 6 && learningPath === 'custom');
  
  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Progress Header */}
      <div className="border-b bg-card/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-2">
            <h1 className="font-bold text-lg">
              <span className="text-primary">FORGE</span> {t('onboarding.header.title').replace('FORGE ', '')}
            </h1>
            <div className="flex items-center gap-3">
              {/* Interface Language Selector - wired to I18nContext so it
                  actually changes the UI language. Before, this component
                  wrote to 'iling-interface-language' which nothing read. */}
              <Select
                value={locale || 'en'}
                onValueChange={(value) => {
                  setLocale(value);
                }}
              >
                <SelectTrigger className="w-auto h-8 px-2 text-xs" data-testid="onboard-interface-lang">
                  <Globe className="h-4 w-4 mr-1 text-primary" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="en">
                    <span className="flex items-center gap-2">
                      <img src="https://flagcdn.com/w20/us.png" alt="US" className="w-4 h-3 object-cover rounded-sm" />
                      English
                    </span>
                  </SelectItem>
                  <SelectItem value="pt-BR">
                    <span className="flex items-center gap-2">
                      <img src="https://flagcdn.com/w20/br.png" alt="BR" className="w-4 h-3 object-cover rounded-sm" />
                      Português
                    </span>
                  </SelectItem>
                  <SelectItem value="es">
                    <span className="flex items-center gap-2">
                      <img src="https://flagcdn.com/w20/es.png" alt="ES" className="w-4 h-3 object-cover rounded-sm" />
                      Español
                    </span>
                  </SelectItem>
                  <SelectItem value="fr">
                    <span className="flex items-center gap-2">
                      <img src="https://flagcdn.com/w20/fr.png" alt="FR" className="w-4 h-3 object-cover rounded-sm" />
                      Français
                    </span>
                  </SelectItem>
                  <SelectItem value="zh">
                    <span className="flex items-center gap-2">
                      <img src="https://flagcdn.com/w20/cn.png" alt="CN" className="w-4 h-3 object-cover rounded-sm" />
                      中文
                    </span>
                  </SelectItem>
                  <SelectItem value="ru">
                    <span className="flex items-center gap-2">
                      <img src="https://flagcdn.com/w20/ru.png" alt="RU" className="w-4 h-3 object-cover rounded-sm" />
                      Русский
                    </span>
                  </SelectItem>
                  <SelectItem value="fa">
                    <span className="flex items-center gap-2">
                      <img src="https://flagcdn.com/w20/ir.png" alt="IR" className="w-4 h-3 object-cover rounded-sm" />
                      فارسی
                    </span>
                  </SelectItem>
                  <SelectItem value="ar">
                    <span className="flex items-center gap-2">
                      <img src="https://flagcdn.com/w20/sa.png" alt="SA" className="w-4 h-3 object-cover rounded-sm" />
                      العربية
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
              {/* Exit Button */}
              <Button
                variant="ghost"
                size="sm"
                className="text-muted-foreground hover:text-destructive"
                onClick={async () => {
                  if (window.confirm(t('onboarding.header.exitConfirm'))) {
                    try {
                      // Mark onboarding as skipped so user can access dashboard
                      await axios.post(`${BACKEND_URL}/api/onboarding/skip`, {}, {
                        withCredentials: true
                      });
                    } catch (err) {
                      console.error('Failed to skip onboarding:', err);
                    }
                    // Clear onboarding state from sessionStorage
                    Object.keys(sessionStorage).filter(k => k.startsWith('onboarding_')).forEach(k => sessionStorage.removeItem(k));
                    window.location.href = '/dashboard';
                  }
                }}
                data-testid="onboard-exit-btn"
              >
                <X className="h-4 w-4" />
              </Button>
              <span className="text-sm text-muted-foreground">
                {getStepLabel()}
              </span>
            </div>
          </div>
          <Progress value={getProgressPercent()} className="h-2" />
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-4xl">
          {renderStep()}
        </div>
      </div>

      {/* Navigation Footer */}
      {showNavFooter && (
        <div className="border-t bg-card/50 backdrop-blur-sm sticky bottom-0">
          <div className="max-w-3xl mx-auto px-4 py-4 flex justify-between">
            <Button
              variant="ghost"
              onClick={handleBack}
              disabled={step === 1 && learningType === 'modern'}
              data-testid="prev-btn"
            >
              <ChevronLeft className="h-4 w-4 mr-2" />
              {t('onboarding.nav.back')}
            </Button>
            
            {step < 5 ? (
              <Button
                onClick={handleNext}
                disabled={!canProceed()}
                data-testid="next-btn"
              >
                {t('onboarding.nav.next')}
                <ChevronRight className="h-4 w-4 ml-2" />
              </Button>
            ) : step === 5 ? (
              <div /> // Path choice step - no Next button
            ) : learningPath === 'custom' ? (
              <Button
                onClick={handleCustomComplete}
                disabled={loading || !canProceed()}
                className="gap-2"
                data-testid="complete-btn"
              >
                {loading ? t('onboarding.nav.processing') : (
                  <>
                    <Sparkles className="h-4 w-4" />
                    {t('onboarding.nav.startLearning')}
                  </>
                )}
              </Button>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}
